import type { Meeting, MeetingPerson } from './meetingTypes.ts';

/**
 * Shape of a single meeting node as selected by `useMeetingsQuery`.
 * Kept structural (not importing the generated type) so the mapper is easy to
 * unit test with plain fixtures; the hook passes the generated nodes in.
 */
export type RawLoomPerson = {
	accountId?: string | null;
	name?: string | null;
	picture?: string | null;
} | null;

export type RawLoomMeeting = {
	id?: string | null;
	title?: string | null;
	startsAt?: string | null;
	recorderId?: string | null;
	recorder?: RawLoomPerson;
	inviteeAris?: ReadonlyArray<string | null> | null;
	invitees?: ReadonlyArray<RawLoomPerson> | null;
	video?: {
		id?: string | null;
		url?: string | null;
		isMeeting?: boolean | null;
		meetingAri?: string | null;
		shortSummary?: string | null;
	} | null;
} | null;

const toPerson = (raw: RawLoomPerson, fallbackAccountId = ''): MeetingPerson => ({
	accountId: raw?.accountId ?? fallbackAccountId,
	displayName: raw?.name ?? 'Unknown',
	avatarUrl: raw?.picture ?? undefined,
});

/**
 * Map a raw `LoomMeeting` node into the UI `Meeting` domain type.
 *
 * The caller is expected to have already filtered out meetings without a
 * playable `video.url`, so `videoUrl`/`videoAri` are safe to default to ''.
 * Action items are intentionally empty here — they are populated by a separate
 * (not-yet-available) query and rendered by the swappable ActionItem boundary.
 */
export const mapLoomMeeting = (raw: RawLoomMeeting): Meeting => ({
	id: raw?.id ?? '',
	title: raw?.title ?? 'Untitled meeting',
	startsAt: raw?.startsAt ?? '',
	recorderId: raw?.recorderId ?? '',
	owner: toPerson(raw?.recorder ?? null, raw?.recorderId ?? ''),
	invitees: (raw?.invitees ?? [])
		.filter((p): p is NonNullable<RawLoomPerson> => Boolean(p))
		.map((p) => toPerson(p)),
	videoAri: raw?.video?.id ?? '',
	videoUrl: raw?.video?.url ?? '',
	summary: raw?.video?.shortSummary ?? undefined,
	actionItems: [],
});
